import React from "react";
import {
  Grid,
  TextField,
  Box,
  Button,
  Typography,
  MenuItem,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Select,
  InputAdornment,
  Divider,
  Paper,
  Container,
  Stepper,
  Step,
  StepLabel,
  Chip,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  Avatar,
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import PersonIcon from "@mui/icons-material/Person";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import DescriptionIcon from "@mui/icons-material/Description";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CloseIcon from "@mui/icons-material/Close";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import "react-toastify/dist/ReactToastify.css";
import CBackDrop from "../../../basecomp/components/CBackDrop";
import VisitorFormVM from "../viewmodal/VisitorFormVM";
import CircularProgress from "@mui/material/CircularProgress";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import HistoryIcon from "@mui/icons-material/History";
import DialogContentText from '@mui/material/DialogContentText';
import InfoIcon from '@mui/icons-material/Info';

const VisitorFormView = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const {
    formData,
    isVerified,
    photoPreview,
    documentPreview,
    openPhotoPreview,
    openDocumentPreview,
    isVerifyEnabled,
    loading,
    selectedCountryCode,
    selectedWhomToVisitCountryCode,
    countryCodes,
    today,
    handleInputChange,
    handleFileChange,
    handleSubmit,
    handleOpenPhotoPreview,
    handleClosePhotoPreview,
    handleOpenDocumentPreview,
    handleCloseDocumentPreview,
    handleVerify,
    isFormComplete,
    setSelectedCountryCode,
    setSelectedWhomToVisitCountryCode,
    isContactLookupEnabled,
    isLookingUp,
    lookupError,
    toggleContactLookup,
    documentUrl,
    verifiedFields,
    autoFilledPerson,
    approverConfirmOpen,
    approverDetails,
    handleApproverConfirm,
    handleApproverReject,
    setApproverConfirmOpen,
    validPassDialogOpen,
    setValidPassDialogOpen,
    validPassDetails
  } = VisitorFormVM();

  return (
    <>
      <CBackDrop cOpen={loading} />
      <Box 
        sx={{ 
          backgroundColor: "#f5f7fa",
          minHeight: "100vh",
          py: { xs: 2, sm: 4, md: 6 },
          px: { xs: 0, sm: 2, md: 3 },
          mt: { xs: 6, sm: 4, md: 5 },
          display: "flex",
          flexDirection: "column"
        }}
      >
        <Container 
          maxWidth="lg" 
          disableGutters={false}
          sx={{ 
            mx: "auto",
            px: { xs: 2, sm: 3 }
          }}
        >
          <Paper
            elevation={3}
            sx={{
              borderRadius: { xs: 2, sm: 2, md: 3 },
              overflow: "hidden",
              backgroundColor: "#fff",
              boxShadow: { 
                xs: "0px 3px 6px rgba(0,0,0,0.1)", 
                sm: "0px 4px 10px rgba(0,0,0,0.1)" 
              },
              width: "100%"
            }}
          >
            {/* Header */}
            <Box 
              sx={{ 
                bgcolor: "#0d47a1", 
                py: { xs: 2, sm: 3 },
                px: { xs: 2, sm: 3, md: 4 },
                color: "white",
              }}
            >
              <Typography
                variant={isMobile ? "h5" : "h4"}
                align="center"
                sx={{
                  fontWeight: 600,
                }}
              >
                Visitor Registration Form
              </Typography>
            </Box>

            {/* Content Area */}
            <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
              {/* Progress Stepper */}
              <Box 
                sx={{ 
                  px: { xs: 0, sm: 2 },
                  mb: { xs: 3, sm: 4 },
                  overflowX: "auto",
                }}
              >
                <Stepper 
                  activeStep={
                    !formData.name ? 0 : 
                    !isVerified ? 1 : 
                    !formData.photo || !formData.document ? 2 : 3
                  } 
                  alternativeLabel 
                  sx={{ 
                    "& .MuiStepConnector-line": {
                      minWidth: isMobile ? "20px" : "40px",
                    },
                    "& .MuiStepLabel-label": {
                      fontSize: isMobile ? "0.7rem" : "0.875rem",
                      mt: isMobile ? 0.5 : 1,
                    },
                    minWidth: isMobile ? 320 : "auto",
                  }}
                >
                  <Step>
                    <StepLabel>Personal Details</StepLabel>
                  </Step>
                  <Step>
                    <StepLabel>Person to Visit</StepLabel>
                  </Step>
                  <Step>
                    <StepLabel>Upload Documents</StepLabel>
                  </Step>
                  <Step>
                    <StepLabel>Submit</StepLabel>
                  </Step>
                </Stepper>
              </Box>

              <form onSubmit={handleSubmit}>
                {/* SECTION 1: Personal Information */}
                <Paper 
                  elevation={0} 
                  sx={{ 
                    p: { xs: 2, sm: 3 }, 
                    mb: { xs: 3, sm: 4 }, 
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderLeft: '4px solid #0d47a1',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 1.5, sm: 2 } }}>
                    <Avatar sx={{ bgcolor: '#1976d2', mr: 1.5 }}>
                      <PersonIcon />
                    </Avatar>
                    <Typography variant={isMobile ? "subtitle1" : "h6"} fontWeight="600">
                      Personal Information
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: { xs: 2, sm: 3 } }} />
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        required
                        label="First Name"
                        name="name"
                        fullWidth
                        value={formData.name}
                        onChange={handleInputChange}
                        onInput={(e) => {
                          e.target.value = e.target.value.replace(/[^a-zA-Z\s]/g, "");
                        }}
                        InputProps={{
                          sx: { borderRadius: 1 }
                        }}
                        size={isMobile ? "small" : "medium"}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        required
                        label="Surname"
                        name="surname"
                        fullWidth
                        value={formData.surname}
                        onChange={handleInputChange}
                        onInput={(e) => {
                          e.target.value = e.target.value.replace(/[^a-zA-Z\s]/g, "");
                        }}
                        InputProps={{
                          sx: { borderRadius: 1 }
                        }}
                        size={isMobile ? "small" : "medium"}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        required
                        label="Contact Number"
                        name="contact"
                        fullWidth
                        value={formData.contact}
                        onChange={handleInputChange}
                        onInput={(e) => {
                          e.target.value = e.target.value.replace(/[^0-9]/g, "");
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Select
                                value={selectedCountryCode}
                                onChange={(e) => setSelectedCountryCode(e.target.value)}
                                sx={{
                                  "& .MuiSelect-select": {
                                    padding: isMobile ? "2px 6px" : "4px 8px",
                                    paddingRight: "24px !important",
                                  },
                                  '& fieldset': { border: 'none' }
                                }}
                                renderValue={(selected) => selected}
                                MenuProps={{
                                  PaperProps: {
                                    style: {
                                      maxHeight: 200,
                                    },
                                  },
                                }}
                                size={isMobile ? "small" : "medium"}
                              >
                                {countryCodes && countryCodes.length > 0 ? (
                                  countryCodes.map((country) => (
                                    <MenuItem
                                      key={country.country_code}
                                      value={country.dial_code}
                                    >
                                      {country.dial_code} - {country.country_name}
                                    </MenuItem>
                                  ))
                                ) : (
                                  <MenuItem disabled>Loading...</MenuItem>
                                )}
                              </Select>
                            </InputAdornment>
                          ),
                          endAdornment: isLookingUp && (
                            <InputAdornment position="end">
                              <CircularProgress size={20} />
                            </InputAdornment>
                          ),
                          sx: { borderRadius: 1 }
                        }}
                        size={isMobile ? "small" : "medium"}
                        helperText={lookupError || (isContactLookupEnabled ? "Previous visitor details will be auto-filled when available" : "Auto-fill is disabled")}
                        FormHelperTextProps={{
                          sx: {
                            color: lookupError ? 'error.main' : 'text.secondary'
                          }
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        required
                        label="Email"
                        name="email"
                        fullWidth
                        value={formData.email}
                        onChange={handleInputChange}
                        error={
                          formData.email &&
                          !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)
                        }
                        helperText={
                          formData.email &&
                          !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)
                            ? "Invalid email format"
                            : ""
                        }
                        InputProps={{
                          sx: { borderRadius: 1 }
                        }}
                        size={isMobile ? "small" : "medium"}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        required
                        label="Visit From"
                        name="visitFrom"
                        type="date"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                          inputProps: { min: today },
                          sx: { borderRadius: 1 }
                        }}
                        value={formData.visitFrom}
                        onChange={handleInputChange}
                        size={isMobile ? "small" : "medium"}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        required
                        label="Visit Till"
                        name="visitTill"
                        type="date"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                          inputProps: { min: formData.visitFrom || today },
                          sx: { borderRadius: 1 }
                        }}
                        value={formData.visitTill}
                        onChange={handleInputChange}
                        size={isMobile ? "small" : "medium"}
                      />
                    </Grid>
                    
                    {/* Daily Access Time Section */}
                    <Grid item xs={12}>
                      <Typography 
                        variant="subtitle2" 
                        sx={{ 
                          mb: 2, 
                          fontWeight: 600, 
                          color: 'primary.main',
                          display: 'flex',
                          alignItems: 'center'
                        }}
                      >
                        <HistoryIcon sx={{ mr: 1, fontSize: 20 }} />
                        Daily Access Hours
                        <Tooltip
                          title="Set your daily access hours. This time range will apply to every day of your visit period."
                          arrow
                          placement="top"
                        >
                          <IconButton size="small" sx={{ ml: 0.5 }}>
                            <HelpOutlineIcon fontSize="small" color="action" />
                          </IconButton>
                        </Tooltip>
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        required
                        label="Daily Access From"
                        name="accessTimeFrom"
                        type="time"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                          inputProps: { 
                            min: "06:00", 
                            max: "22:00",
                            step: "900" // 15 minute intervals
                          },
                          sx: { borderRadius: 1 }
                        }}
                        value={formData.accessTimeFrom || "10:00"}
                        onChange={handleInputChange}
                        size={isMobile ? "small" : "medium"}
                        helperText="Start time each day (10:00 AM - 20:00 PM)"
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        required
                        label="Daily Access Till"
                        name="accessTimeTill"
                        type="time"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                          inputProps: { 
                            min: formData.accessTimeFrom || "06:00", 
                            max: "22:00",
                            step: "900" // 15 minute intervals
                          },
                          sx: { borderRadius: 1 }
                        }}
                        value={formData.accessTimeTill || "20:00"}
                        onChange={handleInputChange}
                        size={isMobile ? "small" : "medium"}
                        helperText="End time each day"
                        error={
                          formData.accessTimeFrom && 
                          formData.accessTimeTill && 
                          formData.accessTimeFrom >= formData.accessTimeTill
                        }
                      />
                    </Grid>
                    
                    
                    <Grid item xs={12}>
                      <TextField
                        required
                        label="Purpose of Visit"
                        name="purpose"
                        fullWidth
                        value={formData.purpose}
                        onChange={handleInputChange}
                        multiline
                        rows={isMobile ? 2 : 3}
                        placeholder="Please describe the purpose of your visit (max 100 words)"
                        helperText="Maximum 100 words"
                        InputProps={{
                          sx: { borderRadius: 1 }
                        }}
                        size={isMobile ? "small" : "medium"}
                      />
                    </Grid>
                  </Grid>
                </Paper>

                {/* SECTION 2: Whom To Visit */}
                <Paper 
                  elevation={0} 
                  sx={{ 
                    p: { xs: 2, sm: 3 }, 
                    mb: { xs: 3, sm: 4 }, 
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderLeft: '4px solid #2e7d32',
                    bgcolor: isVerified ? 'rgba(46, 125, 50, 0.04)' : 'white',
                  }}
                >
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'flex-start', 
                    justifyContent: 'space-between', 
                    mb: { xs: 1.5, sm: 2 },
                    flexDirection: isMobile ? 'column' : 'row',
                  }}>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      mb: isMobile ? 1 : 0,
                    }}>
                      <Avatar sx={{ bgcolor: '#2e7d32', mr: 1.5 }}>
                        <AssignmentIndIcon />
                      </Avatar>
                      <Typography variant={isMobile ? "subtitle1" : "h6"} fontWeight="600">
                        Person to Visit
                        <Tooltip
                          title="You can search by Full Name, Contact Details, or Email ID and click Verify to find the approver."
                          arrow
                          placement="top"
                        >
                          <IconButton size="small" sx={{ ml: 0.5, mt: -0.5 }}>
                            <HelpOutlineIcon fontSize="small" color="action" />
                          </IconButton>
                        </Tooltip>
                      </Typography>
                    </Box>
                    
                    {isVerified && (
                      <Chip 
                        icon={<CheckCircleIcon />} 
                        label="VERIFIED" 
                        color="success" 
                        variant="filled" 
                        size="small"
                        sx={{ fontWeight: 600 }}
                      />
                    )}
                  </Box>
                  <Divider sx={{ mb: { xs: 2, sm: 3 } }} />
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Full Name"
                        name="whomToVisitName"
                        fullWidth
                        value={formData.whomToVisitName}
                        onChange={handleInputChange}
                        disabled={isVerified && !autoFilledPerson}
                        placeholder="Enter the name of the person you want to visit"
                        onInput={(e) => {
                          e.target.value = e.target.value.replace(/[^a-zA-Z\s]/g, "");
                        }}
                        InputProps={{
                          sx: { 
                            borderRadius: 1,
                            "& input": isVerified && !verifiedFields.name ? {
                              filter: "blur(4px)",
                              userSelect: "none"
                            } : {}
                          }
                        }}
                        size={isMobile ? "small" : "medium"}
                        helperText={isVerified && !verifiedFields.name ? "Data blurred for privacy" : ""}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Contact Details"
                        name="whomToVisitContact"
                        fullWidth
                        value={formData.whomToVisitContact}
                        onChange={handleInputChange}
                        disabled={isVerified && !autoFilledPerson}
                        placeholder="Enter contact number"
                        onInput={(e) => {
                          e.target.value = e.target.value.replace(/[^0-9]/g, "");
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Select
                                value={selectedWhomToVisitCountryCode}
                                onChange={(e) => setSelectedWhomToVisitCountryCode(e.target.value)}
                                disabled={isVerified && !autoFilledPerson}
                                sx={{
                                  "& .MuiSelect-select": {
                                    padding: isMobile ? "2px 6px" : "4px 8px",
                                    paddingRight: "24px !important",
                                  },
                                  '& fieldset': { border: 'none' }
                                }}
                                renderValue={(selected) => selected}
                                MenuProps={{
                                  PaperProps: {
                                    style: {
                                      maxHeight: 200,
                                    },
                                  },
                                }}
                                size={isMobile ? "small" : "medium"}
                              >
                                {countryCodes && countryCodes.length > 0 ? (
                                  countryCodes.map((country) => (
                                    <MenuItem
                                      key={country.country_code}
                                      value={country.dial_code}
                                    >
                                      {country.dial_code} - {country.country_name}
                                    </MenuItem>
                                  ))
                                ) : (
                                  <MenuItem disabled>Loading...</MenuItem>
                                )}
                              </Select>
                            </InputAdornment>
                          ),
                          sx: { 
                            borderRadius: 1,
                            "& input": isVerified && !verifiedFields.contact ? {
                              filter: "blur(4px)",
                              userSelect: "none"
                            } : {}
                          }
                        }}
                        size={isMobile ? "small" : "medium"}
                        helperText={isVerified && !verifiedFields.contact ? "Data blurred for privacy" : ""}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Email ID"
                        name="whomToVisitEmail"
                        fullWidth
                        value={formData.whomToVisitEmail}
                        onChange={handleInputChange}
                        disabled={isVerified && !autoFilledPerson}
                        placeholder="Enter email address"
                        error={
                          formData.whomToVisitEmail &&
                          !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.whomToVisitEmail)
                        }
                        helperText={
                          isVerified && !verifiedFields.email ? 
                          "Data blurred for privacy" : 
                          (formData.whomToVisitEmail &&
                          !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.whomToVisitEmail)
                            ? "Invalid email format"
                            : "")
                        }
                        InputProps={{
                          sx: { 
                            borderRadius: 1,
                            "& input": isVerified && !verifiedFields.email ? {
                              filter: "blur(4px)",
                              userSelect: "none"
                            } : {}
                          }
                        }}
                        size={isMobile ? "small" : "medium"}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Button
                        variant={isVerified ? "contained" : "outlined"}
                        color={isVerified ? "success" : "primary"}
                        fullWidth
                        onClick={handleVerify}
                        startIcon={isVerified ? <CheckCircleIcon /> : null}
                        sx={{
                          height: "100%",
                          minHeight: { xs: "40px", md: "53px" },
                          borderRadius: 1,
                          textTransform: "none",
                          fontWeight: 600,
                          width: "100%",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center"
                        }}
                        disabled={!isVerifyEnabled || (isVerified && !autoFilledPerson)}
                        size={isMobile ? "medium" : "large"}
                      >
                        {isVerified ? "Verified" : "Verify"}
                      </Button>
                    </Grid>
                  </Grid>
                </Paper>

                {/* SECTION 3: Upload Documents */}
                <Paper 
                  elevation={0} 
                  sx={{ 
                    p: { xs: 2, sm: 3 }, 
                    mb: { xs: 3, sm: 4 }, 
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderLeft: '4px solid #ed6c02',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 1.5, sm: 2 } }}>
                    <Avatar sx={{ bgcolor: '#ed6c02', mr: 1.5 }}>
                      <DescriptionIcon />
                    </Avatar>
                    <Typography variant={isMobile ? "subtitle1" : "h6"} fontWeight="600">
                      Upload Documents
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: { xs: 2, sm: 3 } }} />
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        select
                        required
                        label="Select Document Type"
                        name="documentType"
                        fullWidth
                        value={formData.documentType}
                        onChange={handleInputChange}
                        helperText="Please select the type of document you'll upload"
                        InputProps={{
                          sx: { borderRadius: 1 }
                        }}
                        size={isMobile ? "small" : "medium"}
                      >
                        <MenuItem value="Aadhar Card">Aadhar Card</MenuItem>
                        <MenuItem value="PAN Card">PAN Card</MenuItem>
                        <MenuItem value="Driving License">Driving License</MenuItem>
                        <MenuItem value="Passport">Passport</MenuItem>
                        <MenuItem value="Voter ID">Voter ID</MenuItem>
                      </TextField>
                    </Grid>
                    
                    {/* Photo Upload Card */}
                    <Grid item xs={12} sm={6}>
                      <Card 
                        elevation={0} 
                        sx={{ 
                          border: '1px solid',
                          borderColor: photoPreview ? '#4caf50' : 'rgba(0, 0, 0, 0.12)',
                          borderRadius: 2,
                          height: '100%',
                          minHeight: { xs: 250, sm: 270 },
                          transition: 'all 0.2s ease-in-out',
                          overflow: 'hidden',
                          display: 'flex',
                          flexDirection: 'column',
                          '&:hover': {
                            borderColor: photoPreview ? '#4caf50' : '#1976d2',
                            boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                          }
                        }}
                      >
                        <CardContent sx={{ 
                          p: 2, 
                          '&:last-child': { pb: 2 },
                          height: '100%',
                          flex: '1 1 auto',
                          display: 'flex',
                          flexDirection: 'column',
                          overflow: 'hidden'
                        }}>
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'space-between',
                            mb: 1.5
                          }}>
                            <Typography variant="subtitle1" fontWeight={600} color="text.primary">
                              Photo Upload*
                            </Typography>
                            {photoPreview && (
                              <Chip 
                                label="Uploaded" 
                                size="small" 
                                color="success" 
                                variant="outlined"
                                icon={<CheckCircleIcon />}
                              />
                            )}
                          </Box>
                          
                          {photoPreview ? (
                            <Box sx={{ 
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              height: '100%',
                            }}>
                              <Box 
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'center',
                                  alignItems: 'center',
                                  flex: '1 1 auto',
                                  width: '100%',
                                  pt: 1,
                                  mb: 2
                                }}
                              >
                                <Box
                                  sx={{
                                    width: { xs: 100, sm: 110 },
                                    height: { xs: 100, sm: 110 },
                                    borderRadius: 1,
                                    overflow: 'hidden',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    backgroundColor: '#f8f8f8'
                                  }}
                                >
                                  <img
                                    src={photoPreview}
                                    alt="Preview"
                                    style={{
                                      width: '100%',
                                      height: '100%',
                                      objectFit: 'contain'
                                    }}
                                  />
                                </Box>
                              </Box>
                              
                              <Box sx={{ 
                                display: 'flex',
                                gap: 1,
                                width: '100%',
                                flex: '0 0 auto'
                              }}>
                                <Button
                                  fullWidth
                                  variant="outlined"
                                  onClick={handleOpenPhotoPreview}
                                  startIcon={<PhotoCameraIcon />}
                                  sx={{ 
                                    borderRadius: 1,
                                    textTransform: 'none'
                                  }}
                                  size="small"
                                >
                                  Preview
                                </Button>
                                <Button
                                  fullWidth
                                  variant="outlined"
                                  component="label"
                                  startIcon={<CloudUploadIcon />}
                                  sx={{ 
                                    borderRadius: 1,
                                    textTransform: 'none'
                                  }}
                                  size="small"
                                >
                                  Change
                                  <input
                                    type="file"
                                    name="photo"
                                    hidden
                                    accept="image/*"
                                    onChange={handleFileChange}
                                  />
                                </Button>
                              </Box>
                            </Box>
                          ) : (
                            <Box sx={{ 
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              py: 3,
                              backgroundColor: 'rgba(0, 0, 0, 0.02)',
                              borderRadius: 1,
                              border: '1px dashed',
                              borderColor: 'divider',
                              height: '100%',
                            }}>
                              <UploadFileIcon color="action" sx={{ fontSize: 40, mb: 1 }} />
                              <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 2, px: 2 }}>
                                Upload a clear passport-size photo
                                <br />
                                (Max size: 2MB)
                              </Typography>
                              <Button
                                variant="contained"
                                component="label"
                                startIcon={<CloudUploadIcon />}
                                sx={{ 
                                  borderRadius: 1,
                                  textTransform: 'none'
                                }}
                                size="small"
                              >
                                Upload Photo
                                <input
                                  type="file"
                                  name="photo"
                                  hidden
                                  accept="image/*"
                                  onChange={handleFileChange}
                                />
                              </Button>
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                    
                    {/* Document Upload Card */}
                    <Grid item xs={12} sm={6}>
                      <Card 
                        elevation={0} 
                        sx={{ 
                          border: '1px solid',
                          borderColor: documentPreview ? '#4caf50' : 'rgba(0, 0, 0, 0.12)',
                          borderRadius: 2,
                          height: '100%',
                          minHeight: { xs: 250, sm: 270 },
                          transition: 'all 0.2s ease-in-out',
                          overflow: 'hidden',
                          display: 'flex',
                          flexDirection: 'column',
                          '&:hover': {
                            borderColor: documentPreview ? '#4caf50' : '#1976d2',
                            boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                          }
                        }}
                      >
                        <CardContent sx={{ 
                          p: 2, 
                          '&:last-child': { pb: 2 },
                          height: '100%',
                          flex: '1 1 auto',
                          display: 'flex',
                          flexDirection: 'column',
                          overflow: 'hidden'
                        }}>
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'space-between',
                            mb: 1.5
                          }}>
                            <Typography variant="subtitle1" fontWeight={600} color="text.primary">
                              Document Upload*
                            </Typography>
                            {documentPreview && (
                              <Chip 
                                label="Uploaded" 
                                size="small" 
                                color="success" 
                                variant="outlined"
                                icon={<CheckCircleIcon />}
                              />
                            )}
                          </Box>
                          
                          {documentPreview ? (
                            <Box sx={{ 
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              height: '100%',
                            }}>
                              <Box 
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'center',
                                  alignItems: 'center',
                                  flex: '1 1 auto',
                                  width: '100%',
                                  pt: 1,
                                  mb: 2
                                }}
                              >
                                <Box
                                  sx={{
                                    width: { xs: 100, sm: 110 },
                                    height: { xs: 100, sm: 110 },
                                    borderRadius: 1,
                                    overflow: 'hidden',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    backgroundColor: '#f8f8f8'
                                  }}
                                >
                                  <img
                                    src={documentPreview}
                                    alt="Preview"
                                    style={{
                                      width: '100%',
                                      height: '100%',
                                      objectFit: 'contain'
                                    }}
                                  />
                                </Box>
                              </Box>
                              
                              {/* Add indicator for previous document */}
                              {documentUrl && documentPreview === documentUrl && (
                                <Chip 
                                  label="Previous document" 
                                  color="info" 
                                  size="small" 
                                  sx={{ mt: 1, mb: 1.5, alignSelf: 'center' }}
                                />
                              )}
                              
                              <Box sx={{ 
                                display: 'flex',
                                gap: 1,
                                width: '100%',
                                flex: '0 0 auto'
                              }}>
                                <Button
                                  fullWidth
                                  variant="outlined"
                                  onClick={handleOpenDocumentPreview}
                                  startIcon={<FileUploadIcon />}
                                  sx={{ 
                                    borderRadius: 1,
                                    textTransform: 'none'
                                  }}
                                  size="small"
                                >
                                  Preview
                                </Button>
                                <Button
                                  fullWidth
                                  variant="outlined"
                                  component="label"
                                  startIcon={<CloudUploadIcon />}
                                  sx={{ 
                                    borderRadius: 1,
                                    textTransform: 'none'
                                  }}
                                  size="small"
                                >
                                  Change
                                  <input
                                    type="file"
                                    name="document"
                                    hidden
                                    accept="image/*"
                                    onChange={handleFileChange}
                                  />
                                </Button>
                              </Box>
                            </Box>
                          ) : (
                            <Box sx={{ 
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              py: 3,
                              backgroundColor: 'rgba(0, 0, 0, 0.02)',
                              borderRadius: 1,
                              border: '1px dashed',
                              borderColor: 'divider',
                              height: '100%',
                            }}>
                              <UploadFileIcon color="action" sx={{ fontSize: 40, mb: 1 }} />
                              <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 2, px: 2 }}>
                                {documentUrl ? "Previous document detected. Upload a new document if needed." : `Upload your ${formData.documentType || "identity document"}`}
                                <br />
                                (Max size: 2MB)
                              </Typography>
                              <Button
                                variant="contained"
                                component="label"
                                startIcon={<CloudUploadIcon />}
                                disabled={!formData.documentType}
                                sx={{ 
                                  borderRadius: 1,
                                  textTransform: 'none'
                                }}
                                size="small"
                              >
                                Upload Document
                                <input
                                  type="file"
                                  name="document"
                                  hidden
                                  accept="image/*"
                                  onChange={handleFileChange}
                                />
                              </Button>
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Paper>

                {/* Submit Button */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    mt: { xs: 3, sm: 4 }
                  }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    size={isMobile ? "medium" : "large"}
                    endIcon={<ArrowForwardIcon />}
                    sx={{
                      px: { xs: 3, sm: 5 },
                      py: { xs: 1, sm: 1.5 },
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: { xs: "0.95rem", sm: "1.1rem" },
                      boxShadow: 3,
                      width: isMobile ? "100%" : "auto",
                      minWidth: { sm: 200 }
                    }}
                    disabled={!isFormComplete()}
                    onClick={handleSubmit}
                  >
                    Submit Registration
                  </Button>
                </Box>
              </form>
            </Box>
          </Paper>
        </Container>
      </Box>

      {/* Photo Preview Dialog */}
      <Dialog
        open={openPhotoPreview}
        onClose={handleClosePhotoPreview}
        maxWidth="md"
        fullScreen={isMobile}
        PaperProps={{
          sx: { borderRadius: isMobile ? 0 : 2 }
        }}
      >
        <DialogTitle 
          sx={{ 
            bgcolor: 'primary.main', 
            color: 'white',
            py: { xs: 1.5, sm: 2 },
            px: { xs: 2, sm: 3 },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Typography variant={isMobile ? "subtitle1" : "h6"}>
            Photo Preview
          </Typography>
          <IconButton
            edge="end"
            color="inherit"
            onClick={handleClosePhotoPreview}
            aria-label="close"
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <img
            src={photoPreview}
            alt="Photo Preview"
            style={{ 
              width: "100%",
              maxHeight: isMobile ? "calc(100vh - 120px)" : "70vh",
              objectFit: "contain"
            }}
          />
        </DialogContent>
        {!isMobile && (
          <DialogActions sx={{ p: 2 }}>
            <Button 
              onClick={handleClosePhotoPreview} 
              color="primary"
              variant="contained"
              size="medium"
              sx={{ borderRadius: 1 }}
            >
              Close
            </Button>
          </DialogActions>
        )}
      </Dialog>

      {/* Document Preview Dialog */}
      <Dialog
        open={openDocumentPreview}
        onClose={handleCloseDocumentPreview}
        maxWidth="md"
        fullScreen={isMobile}
        PaperProps={{
          sx: { borderRadius: isMobile ? 0 : 2 }
        }}
      >
        <DialogTitle 
          sx={{ 
            bgcolor: 'primary.main', 
            color: 'white',
            py: { xs: 1.5, sm: 2 },
            px: { xs: 2, sm: 3 },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Typography variant={isMobile ? "subtitle1" : "h6"}>
            {formData.documentType} Preview
          </Typography>
          <IconButton
            edge="end"
            color="inherit"
            onClick={handleCloseDocumentPreview}
            aria-label="close"
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <img
            src={documentPreview}
            alt="Document Preview"
            style={{ 
              width: "100%",
              maxHeight: isMobile ? "calc(100vh - 120px)" : "70vh",
              objectFit: "contain"
            }}
          />
        </DialogContent>
        {!isMobile && (
          <DialogActions sx={{ p: 2 }}>
            <Button 
              onClick={handleCloseDocumentPreview} 
              color="primary"
              variant="contained"
              size="medium"
              sx={{ borderRadius: 1 }}
            >
              Close
            </Button>
          </DialogActions>
        )}
      </Dialog>

      <Dialog
        open={approverConfirmOpen}
        onClose={() => setApproverConfirmOpen(false)}
        aria-labelledby="approver-confirmation-dialog"
      >
        <DialogTitle id="approver-confirmation-dialog">
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <InfoIcon color="info" sx={{ mr: 1 }} />
            Confirm Approver Selection
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {approverDetails ? (
              <>
                You are verifying a visit to:
                <Box component="div" sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="subtitle1" fontWeight="medium">
                    {approverDetails.fullName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Email: {approverDetails.email}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Contact: {approverDetails.contact_details}
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ mt: 2 }}>
                  If this is not the correct person, click "Not the right person" and verify using their email or contact number instead.
                </Typography>
              </>
            ) : (
              'Please confirm if this is the correct approver.'
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleApproverReject} color="error">
            Not the right person
          </Button>
          <Button onClick={handleApproverConfirm} color="primary" variant="contained" autoFocus>
            Yes, this is correct
          </Button>
        </DialogActions>
      </Dialog>

      {/* Valid Pass Information Dialog */}
      <Dialog
        open={validPassDialogOpen}
        onClose={() => setValidPassDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: isMobile ? 0 : 2 }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: '#ff9800', // Warning color
          color: 'white',
          py: { xs: 1.5, sm: 2 },
          px: { xs: 2, sm: 3 }
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <InfoIcon sx={{ mr: 1.5 }} />
            <Typography variant={isMobile ? "subtitle1" : "h6"}>
              Overlapping Visit Dates
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 2, sm: 3 }, mt: 1 }}>
          {validPassDetails && (
            <>
              <Typography variant="body1" gutterBottom>
                The dates you requested (<strong>{new Date(validPassDetails.requestedFrom).toLocaleDateString()}</strong> to <strong>{new Date(validPassDetails.requestedTill).toLocaleDateString()}</strong>) overlap with an existing approved visitor pass:
              </Typography>
              <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Active From:</Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {new Date(validPassDetails.visit_from).toLocaleDateString()}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Valid Until:</Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {new Date(validPassDetails.visit_till).toLocaleDateString()}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">Person to Visit:</Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {validPassDetails.whom_to_visit}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">Status:</Typography>
                    <Chip 
                      label={validPassDetails.status} 
                      color="success" 
                      size="small" 
                      sx={{ mt: 0.5 }}
                    />
                  </Grid>
                </Grid>
              </Box>
              <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                You do not need to register again for these dates. Your existing pass is valid and can be presented at the entrance.
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={() => setValidPassDialogOpen(false)} 
            variant="contained"
            color="primary"
            startIcon={<CheckCircleIcon />}
            sx={{ borderRadius: 1 }}
          >
            I Understand
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default VisitorFormView;